import { defineStore, storeToRefs } from "pinia";
import type {
    DynamicDialogCloseOptions,
    DynamicDialogInstance,
} from "primevue/dynamicdialogoptions";
import { useDialog } from "primevue/usedialog";
import { computed, onMounted, ref } from "vue";

import ApiKeyCreatedDialog from "@/components/dialogs/ApiKeyCreatedDialog.vue";
import CreateApiKeyDialog from "@/components/dialogs/CreateApiKeyDialog.vue";
import { useServices } from "@/lib/openapi";
import type { ApiKey, ApiKeyCreated } from "@/lib/openapi/gen/types.gen";
import { useAuthenticationStore } from "@/stores/authentication-store";

export type { ApiKey, ApiKeyCreated };

export interface CreateApiKeyResult extends DynamicDialogCloseOptions {
    data: {
        success: boolean;
        apiKey?: ApiKeyCreated;
    };
    error?: unknown;
}

export const useApiKeyStore = defineStore("apiKey", () => {
    const isLoading = ref(false);
    const error = ref<string>();
    const apiKeys = ref<ApiKey[]>([]);
    const initialized = ref(false);

    // Dialog related state
    const dialog = useDialog();
    const dialogRef = ref<DynamicDialogInstance & { visible?: boolean }>();
    const isDialogVisible = computed(() => Boolean(dialogRef.value?.visible));

    const { getToken } = useAuthenticationStore();

    // Use the SDK with the proper requirements
    const api = useServices({
        opts: {
            throwOnError: false,
        },
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });

    const getApiKeys = async () => {
        isLoading.value = true;
        error.value = undefined;

        try {
            // Get auth token and organization ID for direct API call
            const token = await getToken();

            if (!token) {
                throw new Error("No authentication token available");
            }

            const { response, data } = await api.listApiKeysUserApiKeysGet({});

            if (!response?.ok) {
                throw new Error(
                    `Failed to fetch API keys: ${response?.status || "unknown"}`
                );
            }

            apiKeys.value = data?.api_keys || [];
            initialized.value = true;
            return apiKeys.value;
        } catch (err) {
            console.error("Failed to fetch API keys:", err);
            error.value = "Failed to load API keys. Please try again.";
            return [];
        } finally {
            isLoading.value = false;
        }
    };

    onMounted(async () => {
        await getApiKeys();
    });

    const createApiKey = async (name: string, expiresAt?: Date) => {
        isLoading.value = true;
        error.value = undefined;

        try {
            // Get auth token and organization ID for direct API call
            const token = await getToken();

            if (!token) {
                throw new Error("No authentication token available");
            }

            // Make a direct fetch request
            const { response, data } = await api.createApiKeyUserApiKeyPost({
                body: {
                    name: name,
                    expires_at: expiresAt,
                },
            });

            if (!response) {
                // Network error or request failed
                throw new Error(
                    "Failed to create API key: No response received"
                );
            } else if (!response.ok) {
                console.error("API Key creation response error:", {
                    status: response.status,
                    statusText: response.statusText,
                });
                const responseText = await response
                    .text()
                    .catch(() => "No response text");
                throw new Error(
                    `Failed to create API key: ${response.status} - ${responseText}`
                );
            }

            // Set initialized to ensure we know we've made at least one API call
            initialized.value = true;
            // Call getApiKeys directly to refresh the list
            try {
                await getApiKeys();
            } catch (e) {
                console.error("Error refreshing API keys after create:", e);
            }

            return data;
        } catch (err) {
            console.error("Failed to create API key:", err);
            error.value = "Failed to create API key. Please try again.";
            return;
        } finally {
            isLoading.value = false;
        }
    };

    const revokeApiKey = async (apiKeyId: string): Promise<boolean> => {
        isLoading.value = true;
        error.value = undefined;

        try {
            // Get auth token ID for direct API call
            const { getToken } = useAuthenticationStore();
            const token = await getToken();

            if (!token) {
                throw new Error("No authentication token available");
            }

            // Make a direct fetch request
            const { response } = await api.revokeApiKeyUserApiKeyApiKeyIdDelete(
                {
                    path: { api_key_id: apiKeyId },
                }
            );

            if (!response?.ok) {
                throw new Error(
                    `Failed to revoke API key: ${response?.status || "unknown"}`
                );
            }

            console.log("API key revoked successfully");

            // Refresh the list of API keys after revoking (in background)
            try {
                await getApiKeys();
            } catch (e) {
                console.error("Error refreshing API keys after revoke:", e);
            }

            return true;
        } catch (err) {
            console.error("Failed to revoke API key:", err);
            error.value = "Failed to revoke API key. Please try again.";
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    // Dialog functions
    const openCreateApiKeyDialog = () => {
        dialogRef.value = dialog.open(CreateApiKeyDialog, {
            props: {
                header: "Create New API Key",
                style: {
                    width: "520px",
                },
                modal: true,
                dismissableMask: true,
                closeOnEscape: true,
            },
            onClose: (result?: CreateApiKeyResult) => {
                if (result?.data.success && result?.data.apiKey) {
                    openApiKeyCreatedDialog(result.data.apiKey);
                }
            },
        });

        return dialogRef.value;
    };

    const openApiKeyCreatedDialog = (apiKey: ApiKeyCreated) => {
        dialogRef.value = dialog.open(ApiKeyCreatedDialog, {
            props: {
                header: "New API Key Created",
                style: {
                    width: "1080px",
                },
                modal: true,
                closable: false,
                closeOnEscape: false,
            },
            data: {
                apiKey: apiKey,
            },
        });

        return dialogRef.value;
    };

    return {
        apiKeys,
        isLoading,
        error,
        getApiKeys,
        createApiKey,
        revokeApiKey,
        openCreateApiKeyDialog,
        openApiKeyCreatedDialog,
        isDialogVisible,
        refresh: () => getApiKeys(),
    };
});

export const useApiKeyManager = () => storeToRefs(useApiKeyStore());
